import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { OutlinedInput, Typography } from '@material-ui/core';
import { useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import { useGetTableOfContentQuery } from 'src/store/table-of-contents/tableOfContentsApi';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsSolid } from 'src/config/api/models/FontAwesome';
import theme from 'src/config/theme';
import { usePostSearchResultMutation } from 'src/store/search-content/searchContentApi';
import { TOCSearchResults } from './components/SearchResults/TOCSearchResults';
import TOCSearchLoading from './components/SearchLoading/TOCSearchLoading';

import TOCSearchNavigationBar from './components/NavigationBar/TOCSearchNavigationBar';

export const TOCSearch: FC = () => {
  let params = useParams<UrlParams>();
  const { bookId } = params;
  const [search, setSearch] = useState<string>('');
  const { data } = useGetTableOfContentQuery(bookId ?? '', {
    skip: !bookId,
  });
  const [searchContent, response] = usePostSearchResultMutation({ fixedCacheKey: 'toc-search' });
  const { reset } = response;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!bookId) return;
    searchContent({ bookId, body: { search } });
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!response.isUninitialized) {
      setIsLoading(true);
    }
    if (response.error || response.isSuccess) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [response]);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    reset();
  }, [reset]);

  useEffect(() => {
    if (search) return;
    if (!search) {
      reset();
    }
  }, [search, reset]);

  useEffect(
    () => () => {
      setSearch('');
    },
    []
  );

  return (
    <>
      <TOCSearchNavigationBar />
      <div style={{ display: 'flex', gap: 32, flexDirection: 'column' }}>
        <Typography variant="h1"> Search within {data?.title}</Typography>
        <form onSubmit={handleSubmit}>
          <OutlinedInput
            value={search}
            name="table-of-content-search"
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            fullWidth
            placeholder="Add a keyword"
            disabled={response.isLoading}
            endAdornment={
              response.isUninitialized ? (
                <FontAwesomeSpriteIcon
                  style={{ cursor: 'pointer' }}
                  icon={SpriteIconsSolid.MAGNIFYING_GLASS}
                  color={theme.palette.secondary.main}
                  iconSize="md"
                />
              ) : (
                <FontAwesomeSpriteIcon
                  style={{ cursor: 'pointer' }}
                  icon={SpriteIconsSolid.XMARK}
                  color={theme.palette.secondary.main}
                  iconSize="lg"
                  onClick={() => handleClearSearch()}
                />
              )
            }
          />
        </form>
        {isLoading && search && <TOCSearchLoading />}
        {!response.isUninitialized && !isLoading && <TOCSearchResults />}
      </div>
    </>
  );
};
